import React, { FC, HTMLAttributes, useContext, useState } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import * as Yup from 'yup';
import { Formik, FormikValues } from 'formik';
import {
  Box,
  TextField,
  FormHelperText,
  Typography,
  Link,
  Backdrop,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { useSettings } from '@cigam/template/dist/hooks/useSettings';
import { useDispatch } from 'src/store';
import { login } from '../../../slices/account';
import { recuperarSenhaUsuario } from '../../../services/authService';
import { RecuperaSenhaDTO } from '../../../types/dtos/auth';
import { DoisFatoresContext } from '../../../contexts/DoisFatoresContext';

const PREFIX = 'LoginForm';

const classes = {
  root: `${PREFIX}-root`,
  backdrop: `${PREFIX}-backdrop`,
  link: `${PREFIX}-link`,
};

const StyledFormik = styled(Formik)(({ theme }) => ({
  [`& .${classes.root}`]: {},
  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.primary.main,
  },
}));

interface Props extends HTMLAttributes<HTMLFormElement> {
  onSubmitSuccess: () => void;
  handleDoisFatores: () => void;
}

export const LoginForm: FC<Props> = ({
  className,
  onSubmitSuccess,
  handleDoisFatores,
  ...rest
}) => {
  const dispatch = useDispatch();
  const { settings } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);
  const { setUsername, setPassword } = useContext(DoisFatoresContext);
  const portalAtivo = settings.activePortals[0];

  const handleClose = () => {
    setOpenBackdrop(false);
  };

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const checkCapsLock = (event: any) => {
    if (event.getModifierState('CapsLock')) {
      setIsCapsLockOn(true);
    } else {
      setIsCapsLockOn(false);
    }
  };

  const leavePasswordInput = () => {
    setIsCapsLockOn(false);
  };

  const handleClickOpen = async (values: FormikValues) => {
    const dados: RecuperaSenhaDTO = {
      Usuario: values.user,
      Senha: '',
      UrlPortal: settings.activePortals[0],
      Token: '',
      CodigoSenhaCriptografada: '',
      RemoverPortalUrl: true,
    };
    setOpenBackdrop(true);
    try {
      const response = await recuperarSenhaUsuario(dados);
      enqueueSnackbar(response.messages, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(error, {
        variant: 'error',
      });
    }
    setOpenBackdrop(false);
  };

  return (
    <>
      {!openBackdrop && (
        <StyledFormik
          initialValues={{
            user: '',
            password: '',
          }}
          validationSchema={Yup.object().shape({
            user: Yup.string().max(255).required('Usuário é obrigatório'),
            password: Yup.string().max(100).required('Senha é obrigatória'),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              setUsername(values.user);
              setPassword(values.password);
              await dispatch(login(values.user, values.password, portalAtivo));
              onSubmitSuccess();
            } catch (error) {
              let message;
              if (error instanceof Error) message = error.message;
              else message = String(error);
              if (
                message === 'E-mail enviado.' ||
                message === 'E-mail não enviado.'
              ) {
                setUsername(values.user);
                setPassword(values.password);
                handleDoisFatores();
              }
              if (!message) message = 'Conexão inválida com o servidor';

              setStatus({ success: false });
              setErrors({ submit: message });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form
              noValidate
              className={clsx(classes.root, className)}
              onSubmit={handleSubmit}
              {...rest}
            >
              <Typography variant="h4" color="textPrimary">
                Faça login
              </Typography>
              <TextField
                error={Boolean(touched.user && errors.user)}
                fullWidth
                autoFocus
                helperText={touched.user && errors.user}
                label="Usuário"
                margin="normal"
                name="user"
                onBlur={handleBlur}
                onChange={handleChange}
                type="text"
                value={values.user}
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                error={Boolean(touched.password && errors.password)}
                fullWidth
                helperText={touched.password && errors.password}
                label="Senha"
                margin="normal"
                name="password"
                onBlur={e => {
                  handleBlur(e);
                  leavePasswordInput();
                }}
                onClick={checkCapsLock}
                onChange={handleChange}
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                onKeyUp={checkCapsLock}
                InputProps={{
                  inputProps: { maxLength: 100 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {isCapsLockOn && (
                <Typography
                  variant="body2"
                  sx={{ mt: 2, mb: 2, color: '#FF0000' }}
                >
                  Tecla Caps Lock ativada
                </Typography>
              )}
              {/* TODO Recuperação de senha para portalservicos bloqueada no momento por conta de haver o portal
            tanto em React quanto em MVC gerando conflito. Será tratado futuramente. */}
              {portalAtivo !== 'portalservicos' && (
                <Link
                  sx={{ cursor: 'pointer' }}
                  underline="none"
                  color="inherit"
                  variant="body1"
                  onClick={() => handleClickOpen(values)}
                >
                  Esqueceu sua senha?
                </Link>
              )}
              <Box mt={2}>
                <LoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  Entrar
                </LoadingButton>
                {errors.submit && (
                  <Box mt={3}>
                    <FormHelperText error>{errors.submit}</FormHelperText>
                  </Box>
                )}
              </Box>
            </form>
          )}
        </StyledFormik>
      )}
      <Backdrop
        className={classes.backdrop}
        open={openBackdrop}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};
